import { degrees } from 'pdf-lib'
import { trackToolUsage } from '../analytics.js'
import { clamp, hexToRgb } from './utils.js'

const svgNamespace = 'http://www.w3.org/2000/svg'
const svgElement = (name, attributes = {}) => {
  const element = document.createElementNS(svgNamespace, name)
  for (const [key, value] of Object.entries(attributes)) element.setAttribute(key, String(value))
  return element
}

function pointLineDistance(point, start, end) {
  const dx = end.x - start.x
  const dy = end.y - start.y
  if (!dx && !dy) return Math.hypot(point.x - start.x, point.y - start.y)
  const t = clamp(((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy), 0, 1)
  return Math.hypot(point.x - (start.x + t * dx), point.y - (start.y + t * dy))
}

function simplifyPoints(points, tolerance = 0.0015) {
  if (points.length <= 2) return points
  let maxDistance = 0
  let split = 0
  for (let index = 1; index < points.length - 1; index += 1) {
    const distance = pointLineDistance(points[index], points[0], points[points.length - 1])
    if (distance > maxDistance) {
      maxDistance = distance
      split = index
    }
  }
  if (maxDistance <= tolerance) return [points[0], points[points.length - 1]]
  return [
    ...simplifyPoints(points.slice(0, split + 1), tolerance).slice(0, -1),
    ...simplifyPoints(points.slice(split), tolerance),
  ]
}

export const visualAnnotationMethods = {
  initVisualAnnotations() {
    this.markupColorInput = this.markupOptions?.querySelector('.markup-color')
    this.markupOpacityInput = this.markupOptions?.querySelector('.markup-opacity')
    this.markupStrokeInput = this.markupOptions?.querySelector('.markup-stroke')
    const bindOption = (input, key, transform) => {
      if (!input) return
      let snapshot = null
      input.addEventListener('focus', () => { snapshot = this.captureHistoryState() })
      input.addEventListener('input', () => {
        const value = transform(input.value)
        this.markupDefaults[key] = value
        const markup = this.selectedMarkup()
        if (markup) {
          if (snapshot) {
            this.recordHistorySnapshot(snapshot)
            snapshot = null
          }
          markup[key] = value
          this.renderVisualAnnotations(this.visiblePages[this.currentIndex])
          this.updateDirtyState()
        }
      })
    }
    bindOption(this.markupColorInput, 'color', value => value)
    bindOption(this.markupOpacityInput, 'opacity', value => clamp(Number(value) / 100, 0.1, 1))
    bindOption(this.markupStrokeInput, 'strokeWidth', value => clamp(Number(value), 1, 30))

    this.imageInput?.addEventListener('click', () => { this.imageInput.value = '' })
    this.imageInput?.addEventListener('change', () => {
      if (this.imageInput.files?.[0]) trackToolUsage('insert-image', 'file-picker')
      this.insertImageFile(this.imageInput.files?.[0])
    })
    this.initSignaturePad()
  },

  setMarkupMode(type) {
    if (this.editingAnnotationId) this.exitAnnotationEdit()
    if (this.editingTextItemId) this.exitTextEdit()
    if (this.selectedAnnotationId) this.deselectAnnotation()
    if (this.selectedTextItem) this.deselectPdfText()
    this.selectedMarkupId = null
    const nextMode = this.markupMode === type ? null : type
    this.setTextMode(false)
    this.markupMode = nextMode
    this.setSelectMode(!nextMode)
    document.querySelectorAll('.markup-mode').forEach(button => {
      const active = button.getAttribute('data-action') === `markup-${this.markupMode}`
      button.classList.toggle('active', active)
      button.setAttribute('aria-pressed', String(active))
    })
    this.stage.classList.toggle('drawing-markup', Boolean(this.markupMode))
    this.syncMarkupOptions()
    this.renderCurrentPage()
    this.setStatus(this.markupMode ? `${this.markupLabel(this.markupMode)} tool active. Drag on the page.` : 'Markup tool off.')
  },

  markupLabel(type) {
    return ({ highlight: 'Highlight', freehand: 'Freehand', arrow: 'Arrow' })[type] || 'Markup'
  },

  selectMarkup(id) {
    this.selectedMarkupId = id
    this.markupMode = null
    this.setTextMode(false)
    this.setSelectMode(true)
    document.querySelectorAll('.markup-mode').forEach(button => button.classList.remove('active'))
    this.renderVisualAnnotations(this.visiblePages[this.currentIndex])
    this.syncMarkupOptions()
    this.updateDeleteControl()
  },

  selectedMarkup() {
    const pageState = this.visiblePages[this.currentIndex]
    return pageState?.markups?.find(markup => markup.id === this.selectedMarkupId) || null
  },

  syncMarkupOptions() {
    const markup = this.selectedMarkup()
    this.markupOptions.hidden = !this.markupMode && !markup
    const values = markup || this.markupDefaults
    if (this.markupColorInput) this.markupColorInput.value = values.color || '#f4c430'
    if (this.markupOpacityInput) this.markupOpacityInput.value = String(Math.round((values.opacity ?? 1) * 100))
    if (this.markupStrokeInput) this.markupStrokeInput.value = String(values.strokeWidth || 3)
    this.markupOptions?.classList.toggle('image-selected', markup?.type === 'image' || markup?.type === 'signature')
  },

  markupPoint(event) {
    const rect = this.markupLayer.getBoundingClientRect()
    return {
      x: clamp((event.clientX - rect.left) / rect.width, 0, 1),
      y: clamp((event.clientY - rect.top) / rect.height, 0, 1),
    }
  },

  startMarkupInteraction(event) {
    if (event.button !== 0 || !this.pdf) return
    const group = (event.target as Element)?.closest?.('.markup-object')
    if (group && this.selectMode) {
      event.preventDefault()
      event.stopPropagation()
      const id = group.getAttribute('data-markup-id')
      if (id !== this.selectedMarkupId) this.selectMarkup(id)
      const markup = this.selectedMarkup()
      if (!markup) return
      this.markupInteraction = {
        kind: (event.target as Element).classList.contains('markup-resize-handle') ? 'resize' : 'move',
        markup,
        start: this.markupPoint(event),
        original: structuredClone(markup),
        historySnapshot: this.captureHistoryState(),
      }
      return
    }
    if (!this.markupMode || (event.target as Element)?.closest?.('.annotation-tool-panel')) return
    event.preventDefault()
    const pageState = this.visiblePages[this.currentIndex]
    const start = this.markupPoint(event)
    const markup: any = {
      id: `${pageState.id}:markup:${Date.now()}:${pageState.markups.length + 1}`,
      type: this.markupMode,
      color: this.markupDefaults.color,
      opacity: this.markupMode === 'highlight' ? this.markupDefaults.opacity : Math.max(this.markupDefaults.opacity, 0.8),
      strokeWidth: this.markupDefaults.strokeWidth,
    }
    if (this.markupMode === 'freehand') markup.points = [start]
    else if (this.markupMode === 'arrow') Object.assign(markup, { x1: start.x, y1: start.y, x2: start.x, y2: start.y })
    else Object.assign(markup, { x: start.x, y: start.y, width: 0, height: 0 })
    const historySnapshot = this.captureHistoryState()
    pageState.markups.push(markup)
    this.selectedMarkupId = markup.id
    this.markupInteraction = { kind: 'draw', markup, start, historySnapshot }
    this.renderVisualAnnotations(pageState)
  },

  moveMarkupInteraction(event) {
    const interaction = this.markupInteraction
    if (!interaction) return
    const point = this.markupPoint(event)
    const markup = interaction.markup
    if (interaction.kind === 'draw') {
      if (markup.type === 'freehand') {
        const previous = markup.points[markup.points.length - 1]
        if (Math.hypot(point.x - previous.x, point.y - previous.y) > 0.002) markup.points.push(point)
      } else if (markup.type === 'arrow') {
        markup.x2 = point.x
        markup.y2 = point.y
      } else {
        markup.x = Math.min(interaction.start.x, point.x)
        markup.y = Math.min(interaction.start.y, point.y)
        markup.width = Math.abs(point.x - interaction.start.x)
        markup.height = Math.abs(point.y - interaction.start.y)
      }
    } else if (interaction.kind === 'move') {
      const originalBox = this.markupBounds(interaction.original)
      const dx = clamp(point.x - interaction.start.x, -originalBox.x, 1 - originalBox.x - originalBox.width)
      const dy = clamp(point.y - interaction.start.y, -originalBox.y, 1 - originalBox.y - originalBox.height)
      this.translateMarkup(markup, interaction.original, dx, dy)
    } else if (interaction.kind === 'resize') {
      this.resizeMarkup(markup, interaction.original, point)
    }
    this.renderVisualAnnotations(this.visiblePages[this.currentIndex])
  },

  endMarkupInteraction() {
    const interaction = this.markupInteraction
    if (!interaction) return
    const markup = interaction.markup
    if (markup.type === 'freehand') markup.points = simplifyPoints(markup.points)
    const bounds = this.markupBounds(markup)
    if (interaction.kind === 'draw' && bounds.width < 0.004 && bounds.height < 0.004) {
      const pageState = this.visiblePages[this.currentIndex]
      pageState.markups = pageState.markups.filter(item => item !== markup)
      this.selectedMarkupId = null
    } else {
      this.recordHistorySnapshot(interaction.historySnapshot)
      this.updateDirtyState()
      this.setStatus(`${this.markupLabel(markup.type)} added or updated.`)
    }
    this.markupInteraction = null
    this.renderVisualAnnotations(this.visiblePages[this.currentIndex])
    this.syncMarkupOptions()
  },

  markupBounds(markup) {
    if (markup.type === 'freehand') {
      const xs = markup.points.map(point => point.x)
      const ys = markup.points.map(point => point.y)
      const x = Math.min(...xs)
      const y = Math.min(...ys)
      return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y }
    }
    if (markup.type === 'arrow') {
      return { x: Math.min(markup.x1, markup.x2), y: Math.min(markup.y1, markup.y2), width: Math.abs(markup.x2 - markup.x1), height: Math.abs(markup.y2 - markup.y1) }
    }
    return { x: markup.x, y: markup.y, width: markup.width, height: markup.height }
  },

  translateMarkup(markup, original, dx, dy) {
    if (original.type === 'freehand') markup.points = original.points.map(point => ({ x: point.x + dx, y: point.y + dy }))
    else if (original.type === 'arrow') Object.assign(markup, { x1: original.x1 + dx, y1: original.y1 + dy, x2: original.x2 + dx, y2: original.y2 + dy })
    else Object.assign(markup, { x: original.x + dx, y: original.y + dy })
  },

  resizeMarkup(markup, original, point) {
    const box = this.markupBounds(original)
    const width = clamp(point.x - box.x, 0.01, 1 - box.x)
    let height = clamp(point.y - box.y, 0.01, 1 - box.y)
    if (original.type === 'image' || original.type === 'signature') {
      const layerRect = this.markupLayer.getBoundingClientRect()
      height = width * layerRect.width / (original.aspectRatio * layerRect.height)
      Object.assign(markup, { x: box.x, y: box.y, width, height: Math.min(height, 1 - box.y) })
      return
    }
    const scaleX = width / Math.max(box.width, 0.001)
    const scaleY = height / Math.max(box.height, 0.001)
    if (original.type === 'freehand') markup.points = original.points.map(p => ({ x: box.x + (p.x - box.x) * scaleX, y: box.y + (p.y - box.y) * scaleY }))
    else if (original.type === 'arrow') Object.assign(markup, { x1: box.x + (original.x1 - box.x) * scaleX, y1: box.y + (original.y1 - box.y) * scaleY, x2: box.x + (original.x2 - box.x) * scaleX, y2: box.y + (original.y2 - box.y) * scaleY })
    else Object.assign(markup, { x: box.x, y: box.y, width, height })
  },

  renderVisualAnnotations(pageState) {
    if (!pageState || !this.markupLayer) return
    pageState.markups ||= []
    this.markupLayer.innerHTML = ''
    const rect = this.markupLayer.getBoundingClientRect()
    const width = rect.width || Number(this.markupLayer.getAttribute('width')) || 1
    const height = rect.height || Number(this.markupLayer.getAttribute('height')) || 1
    for (const [stackIndex, markup] of pageState.markups.entries()) {
      const group = svgElement('g', {
        class: 'markup-object',
        'data-markup-id': markup.id,
        'data-stack-index': stackIndex + 1,
      })
      const hitBounds = this.markupBounds(markup)
      const hitPadding = Math.max(6, markup.strokeWidth * this.zoom * 2)
      group.append(svgElement('rect', {
        class: 'markup-hit-area',
        x: hitBounds.x * width - hitPadding,
        y: hitBounds.y * height - hitPadding,
        width: hitBounds.width * width + hitPadding * 2,
        height: hitBounds.height * height + hitPadding * 2,
      }))
      if (markup.type === 'highlight') {
        group.append(svgElement('rect', {
          x: markup.x * width, y: markup.y * height, width: markup.width * width, height: markup.height * height,
          fill: markup.color,
          stroke: 'none',
          'stroke-width': markup.strokeWidth * this.zoom, opacity: markup.opacity,
          'vector-effect': 'non-scaling-stroke',
        }))
      } else if (markup.type === 'freehand') {
        group.append(svgElement('polyline', {
          points: markup.points.map(point => `${point.x * width},${point.y * height}`).join(' '),
          fill: 'none', stroke: markup.color, 'stroke-width': markup.strokeWidth * this.zoom, opacity: markup.opacity,
          'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'vector-effect': 'non-scaling-stroke',
        }))
      } else if (markup.type === 'arrow') {
        const x1 = markup.x1 * width, y1 = markup.y1 * height, x2 = markup.x2 * width, y2 = markup.y2 * height
        const angle = Math.atan2(y2 - y1, x2 - x1)
        const head = Math.max(10, markup.strokeWidth * 4)
        group.append(svgElement('line', { x1, y1, x2, y2, stroke: markup.color, 'stroke-width': markup.strokeWidth * this.zoom, opacity: markup.opacity, 'stroke-linecap': 'round', 'vector-effect': 'non-scaling-stroke' }))
        group.append(svgElement('polyline', { points: `${x2 - head * Math.cos(angle - Math.PI / 6)},${y2 - head * Math.sin(angle - Math.PI / 6)} ${x2},${y2} ${x2 - head * Math.cos(angle + Math.PI / 6)},${y2 - head * Math.sin(angle + Math.PI / 6)}`, fill: 'none', stroke: markup.color, 'stroke-width': markup.strokeWidth * this.zoom, opacity: markup.opacity, 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'vector-effect': 'non-scaling-stroke' }))
      } else if (markup.type === 'image' || markup.type === 'signature') {
        const x = markup.x * width, y = markup.y * height, imageWidth = markup.width * width, imageHeight = markup.height * height
        group.append(svgElement('image', { href: markup.dataUrl, x, y, width: imageWidth, height: imageHeight, transform: `rotate(${markup.rotation || 0} ${x + imageWidth / 2} ${y + imageHeight / 2})`, opacity: markup.opacity ?? 1, preserveAspectRatio: 'none' }))
      }
      this.markupLayer.append(group)
      if (markup.id === this.selectedMarkupId) {
        const box = this.markupBounds(markup)
        this.markupLayer.append(svgElement('rect', { class: 'markup-selection', x: box.x * width, y: box.y * height, width: box.width * width, height: box.height * height }))
        this.markupLayer.append(svgElement('rect', { class: 'markup-resize-handle markup-object', 'data-markup-id': markup.id, x: (box.x + box.width) * width - 5, y: (box.y + box.height) * height - 5, width: 10, height: 10 }))
      }
    }
  },

  deleteSelectedMarkup() {
    const pageState = this.visiblePages[this.currentIndex]
    const markup = this.selectedMarkup()
    if (!pageState || !markup) return false
    this.recordHistory()
    pageState.markups = pageState.markups.filter(item => item !== markup)
    this.selectedMarkupId = null
    this.renderVisualAnnotations(pageState)
    this.syncMarkupOptions()
    this.updateDirtyState()
    this.setStatus('Visual object deleted.')
    return true
  },

  duplicateSelectedMarkup() {
    const pageState = this.visiblePages[this.currentIndex]
    const markup = this.selectedMarkup()
    if (!pageState || !markup) return
    this.recordHistory()
    const copy = structuredClone(markup)
    copy.id = `${pageState.id}:markup:${Date.now()}:${pageState.markups.length + 1}`
    this.translateMarkup(copy, copy, 0.025, 0.025)
    pageState.markups.push(copy)
    this.selectedMarkupId = copy.id
    this.renderVisualAnnotations(pageState)
    this.updateDirtyState()
    this.setStatus('Visual object duplicated.')
  },

  async rotateSelectedMarkup() {
    const markup = this.selectedMarkup()
    if (!markup || !['image', 'signature'].includes(markup.type)) return
    this.recordHistory()
    const image = new Image()
    image.src = markup.dataUrl
    await image.decode()
    const canvas = document.createElement('canvas')
    canvas.width = image.naturalHeight
    canvas.height = image.naturalWidth
    const ctx = canvas.getContext('2d')
    ctx.translate(canvas.width, 0)
    ctx.rotate(Math.PI / 2)
    ctx.drawImage(image, 0, 0)
    markup.dataUrl = canvas.toDataURL('image/png')
    markup.imageFormat = 'png'
    markup.aspectRatio = 1 / markup.aspectRatio
    const layer = this.markupLayer.getBoundingClientRect()
    const centerX = markup.x + markup.width / 2
    const centerY = markup.y + markup.height / 2
    const width = markup.height * layer.height / layer.width
    const height = markup.width * layer.width / layer.height
    markup.width = width
    markup.height = height
    markup.x = clamp(centerX - width / 2, 0, 1 - width)
    markup.y = clamp(centerY - height / 2, 0, 1 - height)
    markup.rotation = 0
    this.renderVisualAnnotations(this.visiblePages[this.currentIndex])
    this.updateDirtyState()
    this.setStatus('Image rotated 90°.')
  },

  async insertImageFile(file) {
    if (!file || !this.pdf || !/^image\/(png|jpeg)$/.test(file.type)) {
      if (file) this.setStatus('Please choose a PNG or JPEG image.')
      return
    }
    const result = await this.imageDataForFile(file)
    if (!result) return
    this.addImageMarkup({ ...result, type: 'image' })
  },

  async imageDataForFile(file) {
    const objectUrl = URL.createObjectURL(file)
    try {
      const image = new Image()
      image.src = objectUrl
      await image.decode()
      const maxDimension = 2000
      const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight))
      const canvas = document.createElement('canvas')
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale))
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale))
      canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height)
      const dataUrl = file.type === 'image/jpeg' ? canvas.toDataURL('image/jpeg', 0.88) : canvas.toDataURL('image/png')
      if (scale < 1) this.setStatus(`Large image downscaled to ${canvas.width} × ${canvas.height} for a smaller PDF.`)
      return { dataUrl, aspectRatio: canvas.width / canvas.height, imageFormat: file.type === 'image/jpeg' ? 'jpeg' : 'png' }
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
  },

  addImageMarkup({ dataUrl, aspectRatio, imageFormat = 'png', type = 'image' }) {
    const pageState = this.visiblePages[this.currentIndex]
    if (!pageState) return
    const rect = this.markupLayer.getBoundingClientRect()
    const width = Math.min(0.45, 500 / Math.max(rect.width, 1))
    const height = Math.min(0.45, width * rect.width / (aspectRatio * rect.height))
    const markup = {
      id: `${pageState.id}:markup:${Date.now()}:${pageState.markups.length + 1}`,
      type, dataUrl, imageFormat, aspectRatio, rotation: 0, opacity: 1,
      x: (1 - width) / 2, y: (1 - height) / 2, width, height,
      color: '#000000', strokeWidth: 1,
    }
    this.recordHistory()
    pageState.markups.push(markup)
    this.selectMarkup(markup.id)
    this.updateDirtyState()
    this.setStatus(type === 'signature' ? 'Signature inserted. Drag or resize it before downloading.' : 'Image inserted. Drag or resize it before downloading.')
  },

  initSignaturePad() {
    this.signatureDialog = document.querySelector('.signature-dialog')
    this.signatureCanvas = document.querySelector('.signature-canvas')
    const ctx = this.signatureCanvas?.getContext('2d')
    let drawing = false
    const point = event => {
      const rect = this.signatureCanvas.getBoundingClientRect()
      return { x: (event.clientX - rect.left) * this.signatureCanvas.width / rect.width, y: (event.clientY - rect.top) * this.signatureCanvas.height / rect.height }
    }
    this.signatureCanvas?.addEventListener('pointerdown', event => {
      drawing = true
      const p = point(event)
      ctx.beginPath()
      ctx.moveTo(p.x, p.y)
      this.signatureCanvas.setPointerCapture(event.pointerId)
    })
    this.signatureCanvas?.addEventListener('pointermove', event => {
      if (!drawing) return
      const p = point(event)
      ctx.strokeStyle = '#111820'
      ctx.lineWidth = 4
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.lineTo(p.x, p.y)
      ctx.stroke()
    })
    this.signatureCanvas?.addEventListener('pointerup', () => { drawing = false })
    this.signatureDialog?.querySelector('.signature-clear')?.addEventListener('click', () => ctx.clearRect(0, 0, this.signatureCanvas.width, this.signatureCanvas.height))
    this.signatureDialog?.querySelector('.signature-cancel')?.addEventListener('click', () => this.signatureDialog.close())
    this.signatureDialog?.querySelector('.signature-insert')?.addEventListener('click', () => {
      const pixels = ctx.getImageData(0, 0, this.signatureCanvas.width, this.signatureCanvas.height).data
      if (!pixels.some((value, index) => index % 4 === 3 && value)) {
        this.setStatus('Draw a signature before inserting it.')
        return
      }
      const dataUrl = this.signatureCanvas.toDataURL('image/png')
      this.signatureDialog.close()
      this.addImageMarkup({ dataUrl, aspectRatio: this.signatureCanvas.width / this.signatureCanvas.height, type: 'signature' })
    })
  },

  openSignaturePad() {
    if (!this.pdf) return
    const ctx = this.signatureCanvas.getContext('2d')
    ctx.clearRect(0, 0, this.signatureCanvas.width, this.signatureCanvas.height)
    this.signatureDialog.showModal()
  },

  displayPointToPdf(point, width, height, rotation) {
    const normalizedRotation = ((rotation % 360) + 360) % 360
    const displayWidth = normalizedRotation === 90 || normalizedRotation === 270 ? height : width
    const displayHeight = normalizedRotation === 90 || normalizedRotation === 270 ? width : height
    const x = point.x * displayWidth
    const y = point.y * displayHeight
    if (normalizedRotation === 90) return { x: y, y: x }
    if (normalizedRotation === 180) return { x: width - x, y }
    if (normalizedRotation === 270) return { x: width - y, y: height - x }
    return { x, y: height - y }
  },

  markupPdfBounds(markup, width, height, rotation) {
    const box = this.markupBounds(markup)
    const corners = [
      { x: box.x, y: box.y },
      { x: box.x + box.width, y: box.y },
      { x: box.x, y: box.y + box.height },
      { x: box.x + box.width, y: box.y + box.height },
    ].map(point => this.displayPointToPdf(point, width, height, rotation))
    const xs = corners.map(point => point.x)
    const ys = corners.map(point => point.y)
    return { x: Math.min(...xs), y: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) }
  },

  async drawVisualAnnotationsToPdf(outDoc, page, pageState, rotation) {
    const { width, height } = page.getSize()
    const imageCache = new Map()
    const embedImage = async markup => {
      if (!imageCache.has(markup.dataUrl)) {
        imageCache.set(markup.dataUrl, markup.imageFormat === 'jpeg' ? await outDoc.embedJpg(markup.dataUrl) : await outDoc.embedPng(markup.dataUrl))
      }
      return imageCache.get(markup.dataUrl)
    }
    for (const markup of pageState.markups || []) {
      const color = hexToRgb(markup.color || '#111820')
      if (markup.type === 'highlight') {
        const box = this.markupPdfBounds(markup, width, height, rotation)
        page.drawRectangle({ ...box, color, opacity: markup.opacity })
      } else if (markup.type === 'freehand') {
        const points = markup.points.map(point => this.displayPointToPdf(point, width, height, rotation))
        for (let index = 1; index < points.length; index += 1) {
          page.drawLine({ start: points[index - 1], end: points[index], thickness: markup.strokeWidth, color, opacity: markup.opacity, lineCap: 1 })
        }
      } else if (markup.type === 'arrow') {
        const start = this.displayPointToPdf({ x: markup.x1, y: markup.y1 }, width, height, rotation)
        const end = this.displayPointToPdf({ x: markup.x2, y: markup.y2 }, width, height, rotation)
        page.drawLine({ start, end, thickness: markup.strokeWidth, color, opacity: markup.opacity })
        const angle = Math.atan2(end.y - start.y, end.x - start.x)
        const head = Math.max(8, markup.strokeWidth * 4)
        for (const offset of [-Math.PI / 6, Math.PI / 6]) {
          page.drawLine({ start: end, end: { x: end.x - head * Math.cos(angle + offset), y: end.y - head * Math.sin(angle + offset) }, thickness: markup.strokeWidth, color, opacity: markup.opacity })
        }
      } else if (markup.type === 'image' || markup.type === 'signature') {
        const box = this.markupPdfBounds(markup, width, height, rotation)
        page.drawImage(await embedImage(markup), { ...box, opacity: markup.opacity ?? 1, rotate: degrees(0) })
      }
    }
  },

  async drawVisualAnnotationsToCanvas(ctx, pageState, width, height, renderScale = 1) {
    ctx.save()
    for (const markup of pageState.markups || []) {
      ctx.globalAlpha = markup.opacity ?? 1
      ctx.strokeStyle = markup.color || '#111820'
      ctx.fillStyle = markup.color || '#111820'
      ctx.lineWidth = markup.strokeWidth * renderScale
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      if (markup.type === 'highlight') ctx.fillRect(markup.x * width, markup.y * height, markup.width * width, markup.height * height)
      else if (markup.type === 'freehand') {
        ctx.beginPath()
        markup.points.forEach((point, index) => index ? ctx.lineTo(point.x * width, point.y * height) : ctx.moveTo(point.x * width, point.y * height))
        ctx.stroke()
      } else if (markup.type === 'arrow') {
        const x1 = markup.x1 * width, y1 = markup.y1 * height, x2 = markup.x2 * width, y2 = markup.y2 * height
        const angle = Math.atan2(y2 - y1, x2 - x1)
        const head = Math.max(10 * renderScale, markup.strokeWidth * renderScale * 4)
        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)
        ctx.moveTo(x2, y2)
        ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 6), y2 - head * Math.sin(angle - Math.PI / 6))
        ctx.moveTo(x2, y2)
        ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 6), y2 - head * Math.sin(angle + Math.PI / 6))
        ctx.stroke()
      } else if (markup.type === 'image' || markup.type === 'signature') {
        const image = new Image()
        image.src = markup.dataUrl
        await image.decode()
        ctx.drawImage(image, markup.x * width, markup.y * height, markup.width * width, markup.height * height)
      }
    }
    ctx.restore()
  },
}
